import { useCallback, useRef, useState } from "react";
import type { ECharts } from "echarts";
import { captureEChartsPng } from "@/features/deployment-dashboard/analytics/export/analytics-export.capture";
import { createAnalyticsPdf } from "@/features/deployment-dashboard/analytics/export/analytics-export.pdf";
import {
  getAnalyticsExportFilename,
  isAnalyticsExportWorkerFailure,
  isAnalyticsExportWorkerSuccess,
} from "@/features/deployment-dashboard/analytics/export/analytics-export.utils";
import type { TotalPopulationTrendUnconfiguredArea } from "@situate/contracts/runtime";
import type {
  AnalyticsExportChartSnapshot,
  AnalyticsExportPopulationScope,
  AnalyticsExportReportMetadata,
  AnalyticsExportTheme,
  ExportChartAvailability,
  ExportChartId,
  ExportFormat,
} from "@/features/deployment-dashboard/analytics/export/types";
import type { AnalyticsFilters, AreaLocation } from "@/features/deployment-dashboard/types";
import type { DensityTrendPoint } from "@/features/deployment-dashboard/widgets/charts/density-trend";
import type { PopulationTrendAnalyticsPoint } from "@/features/deployment-dashboard/widgets/charts/population-trend-analytics";
import type {
  ThroughputLineOption,
  ThroughputLineTrendData,
} from "@/features/deployment-dashboard/widgets/charts/throughput-trend";
import { getAnalyticsAreaLabel } from "@/features/deployment-dashboard/shared/analytics-filters";

interface AnalyticsChartState<T> {
  data: T[];
  loading: boolean;
  error: Error | null;
}

interface PopulationTrendChartState extends AnalyticsChartState<PopulationTrendAnalyticsPoint> {
  requestedAreaIds?: readonly string[];
  configuredAreaIds?: readonly string[];
  unconfiguredAreas?: readonly TotalPopulationTrendUnconfiguredArea[];
}

export interface UseAnalyticsExportOptions {
  filters: AnalyticsFilters;
  areas: AreaLocation[];
  populationTrend: PopulationTrendChartState;
  densityTrend: AnalyticsChartState<DensityTrendPoint>;
  throughputLineTrendData: ThroughputLineTrendData[];
  throughputAllLineTrendData: ThroughputLineTrendData[];
  throughputLineTrendLoading: boolean;
  throughputLineTrendError: Error | null;
  throughputLines: ThroughputLineOption[];
  throughputLinesLoading: boolean;
  throughputLinesError: Error | null;
  selectedThroughputLines: ThroughputLineOption[];
  chartInstances: Readonly<Record<ExportChartId, ECharts | null>>;
  theme: AnalyticsExportTheme;
}

export function useAnalyticsExport(options: UseAnalyticsExportOptions) {
  const [isExporting, setIsExporting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const exportingRef = useRef(false);

  const clearError = useCallback(() => setErrorMessage(null), []);

  const startExport = useCallback(
    async (format: ExportFormat, selectedCharts: readonly ExportChartId[]): Promise<boolean> => {
      if (exportingRef.current || selectedCharts.length === 0) return false;

      exportingRef.current = true;
      setIsExporting(true);
      setErrorMessage(null);
      let worker: Worker | undefined;

      try {
        const exportedAt = new Date();
        const metadata = createMetadata(options, exportedAt);
        const charts = createChartSnapshot(options, selectedCharts, format);
        const request = {
          format,
          selectedCharts: [...selectedCharts],
          charts,
          metadata,
          theme: options.theme,
        };

        if (format === "pdf") {
          const file = await createAnalyticsPdf(
            request.charts,
            request.selectedCharts,
            metadata,
            request.theme
          );
          downloadBlob(file, getAnalyticsExportFilename(format, metadata.exportedAtIso));
          return true;
        }

        worker = new Worker(new URL("./export/analytics-export.worker.ts", import.meta.url), {
          type: "module",
        });
        const result = await runWorker(worker, request);
        if (isAnalyticsExportWorkerFailure(result)) throw new Error(result.message);
        if (!isAnalyticsExportWorkerSuccess(result))
          throw new Error("The export response was invalid.");

        downloadBlob(result.file, getAnalyticsExportFilename(format, metadata.exportedAtIso));
        return true;
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : "The analytics export failed.");
        return false;
      } finally {
        worker?.terminate();
        exportingRef.current = false;
        setIsExporting(false);
      }
    },
    [options]
  );

  return { isExporting, errorMessage, clearError, startExport };
}

function createMetadata(
  options: UseAnalyticsExportOptions,
  exportedAt: Date
): AnalyticsExportReportMetadata {
  const throughputLines = options.selectedThroughputLines;
  return {
    dateRangeLabel: `${formatDateTime(options.filters.startDate)} – ${formatDateTime(options.filters.endDate)}`,
    areaLabel: getAnalyticsAreaLabel(options.filters.areaId, options.areas),
    exportedAtLabel: exportedAt.toLocaleString(),
    filterStartIso: options.filters.startDate.toISOString(),
    filterEndIso: options.filters.endDate.toISOString(),
    exportedAtIso: exportedAt.toISOString(),
    ...(throughputLines.length === 0
      ? {}
      : {
          throughputLines: throughputLines.map((line) => ({
            id: line.line_id,
            name: line.line_name,
            areaId: line.areaId,
            areaName: line.areaName,
            positiveDirectionLabel: line.positive_direction.label,
            negativeDirectionLabel: line.negative_direction.label,
          })),
        }),
  };
}

function formatDateTime(date: Date): string {
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function createChartSnapshot(
  options: UseAnalyticsExportOptions,
  selectedCharts: readonly ExportChartId[],
  format: ExportFormat
): AnalyticsExportChartSnapshot[] {
  const selected = new Set(selectedCharts);
  const snapshots: AnalyticsExportChartSnapshot[] = [
    createPopulationSnapshot(options, selected.has("population-trend"), format),
    createDensitySnapshot(options, selected.has("density-trend"), format),
    createThroughputSnapshot(options, selected.has("throughput-trend"), format),
  ];
  return snapshots;
}

function createPopulationSnapshot(
  options: UseAnalyticsExportOptions,
  selected: boolean,
  format: ExportFormat
): AnalyticsExportChartSnapshot {
  const availability = getAvailability(options.populationTrend, selected);
  if (availability !== "ready") return { id: "population-trend", availability };
  const image = captureImage(options.chartInstances["population-trend"], options.theme, format);
  if (format !== "csv" && image === undefined)
    return { id: "population-trend", availability: "not-rendered" };
  return {
    id: "population-trend",
    availability: "ready",
    data: [...options.populationTrend.data],
    populationScope: createPopulationScope(options.populationTrend),
    ...(image === undefined ? {} : { image }),
  };
}

function createPopulationScope(
  populationTrend: UseAnalyticsExportOptions["populationTrend"]
): AnalyticsExportPopulationScope {
  const requestedAreaIds = populationTrend.requestedAreaIds ?? [];
  const configuredAreaIds = populationTrend.configuredAreaIds ?? requestedAreaIds;
  return {
    requestedAreaIds: [...requestedAreaIds],
    configuredAreaIds: [...configuredAreaIds],
    unconfiguredAreas: (populationTrend.unconfiguredAreas ?? []).map((area) => ({
      area_id: area.area_id,
      area_name: area.area_name,
    })),
  };
}

function createDensitySnapshot(
  options: UseAnalyticsExportOptions,
  selected: boolean,
  format: ExportFormat
): AnalyticsExportChartSnapshot {
  const availability = getAvailability(options.densityTrend, selected);
  if (availability !== "ready") return { id: "density-trend", availability };
  const image = captureImage(options.chartInstances["density-trend"], options.theme, format);
  if (format !== "csv" && image === undefined)
    return { id: "density-trend", availability: "not-rendered" };
  return {
    id: "density-trend",
    availability: "ready",
    data: [...options.densityTrend.data],
    ...(image === undefined ? {} : { image }),
  };
}

function createThroughputSnapshot(
  options: UseAnalyticsExportOptions,
  selected: boolean,
  format: ExportFormat
): AnalyticsExportChartSnapshot {
  const selectedLines = options.selectedThroughputLines;
  const linesForExport =
    format === "csv" ? options.throughputAllLineTrendData : options.throughputLineTrendData;
  let availability: ExportChartAvailability = selected ? "ready" : "not-selected";
  if (selected && options.throughputLinesLoading) availability = "loading";
  else if (selected && options.throughputLinesError != null) availability = "error";
  else if (selected && options.throughputLines.length === 0) availability = "not-configured";
  else if (selected && selectedLines.length === 0) availability = "not-selected";
  else if (selected && options.throughputLineTrendLoading) availability = "loading";
  else if (selected && options.throughputLineTrendError != null) availability = "error";
  else if (selected && options.throughputLineTrendData.every((line) => line.points.length === 0))
    availability = "empty";

  if (availability !== "ready" || selectedLines.length === 0) {
    return {
      id: "throughput-trend",
      availability: availability === "ready" ? "not-selected" : availability,
    };
  }
  const image = captureImage(options.chartInstances["throughput-trend"], options.theme, format);
  return {
    id: "throughput-trend",
    availability: image === undefined && format !== "csv" ? "not-rendered" : "ready",
    lines: linesForExport.map(({ line, points }) => ({
      line: { ...line },
      points: points.map((point) => ({ ...point })),
    })),
    ...(image === undefined ? {} : { image }),
  };
}

function getAvailability<T>(state: AnalyticsChartState<T>, selected: boolean) {
  if (!selected) return "not-selected" as const;
  if (state.loading) return "loading" as const;
  if (state.error != null) return "error" as const;
  if (state.data.length === 0) return "empty" as const;
  return "ready" as const;
}

function captureImage(
  instance: ECharts | null,
  theme: AnalyticsExportTheme,
  format: ExportFormat
): Blob | undefined {
  if (format === "csv" || instance == null || instance.isDisposed()) return undefined;
  return captureEChartsPng(instance, theme);
}

function runWorker(worker: Worker, request: object): Promise<unknown> {
  return new Promise((resolve, reject) => {
    worker.onmessage = (event: MessageEvent<unknown>) => resolve(event.data);
    worker.onerror = () => reject(new Error("The analytics export could not be created."));
    worker.onmessageerror = () =>
      reject(new Error("The analytics export response could not be read."));
    worker.postMessage(request);
  });
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  try {
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}
